import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import { primaryText, secondaryText } from '../../../../styling/stylingHelper';
import {
  FilterBlock as FilterBlockType,
  HydratedBlock,
  HydratedSpec,
} from '../../../../services/admin/cloudAgentTypes';
import { KpiStatBlock } from './blocks/KpiStatBlock';
import { FilterBlock } from './blocks/FilterBlock';
import { LineChartBlock } from './blocks/LineChartBlock';
import { BarChartBlock } from './blocks/BarChartBlock';
import { DonutBlock } from './blocks/DonutBlock';
import { RankBarsBlock } from './blocks/RankBarsBlock';
import { ScatterBlock } from './blocks/ScatterBlock';
import { TableBlock } from './blocks/TableBlock';
import { CalloutBlockAdapter } from './blocks/CalloutBlock';
import { SectionHeadingBlockAdapter } from './blocks/SectionHeadingBlock';
import { BlockFrame } from './blocks/BlockFrame';

/** Block types wide enough to be worth a "Full width" toggle (charts + tables). */
const WIDE_BLOCK_TYPES = new Set(['lineChart', 'barChart', 'donut', 'rankBars', 'scatter', 'table']);

/** Graceful placeholder for an unrecognized block type — never crash, never eval. */
const UnknownBlockPlaceholder: React.FC<{ type: string }> = ({ type }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-dashed border-border dark:border-border-dark p-4">
      <p className={cn(secondaryText, 'text-sm')}>{t('cloudAgent.unknownBlock', { type })}</p>
    </div>
  );
};

/**
 * Pure registry switch. The `default` branch's `never`-typed argument makes this
 * exhaustive at compile time: adding a `BlockType` without a case is a typecheck error.
 * At runtime an out-of-contract block (bad server data) still degrades gracefully.
 */
function renderUnknownBlock(block: never): React.ReactNode {
  const type = (block as { type?: string })?.type ?? 'unknown';
  // Frontend logs errors only — an unknown block means the contract drifted.
  console.error(`[CloudAgent] Unknown block type: ${type}`);
  return <UnknownBlockPlaceholder type={type} />;
}

/** Callback fired when a `filter` block's selection changes (page turns it into a rehydrate). */
export type OnFilterChange = (block: FilterBlockType, selected: string[]) => void;

function renderBlock(block: HydratedBlock, onFilterChange?: OnFilterChange): React.ReactNode {
  switch (block.type) {
    case 'kpiStat':
      return <KpiStatBlock block={block} />;
    case 'lineChart':
      return <LineChartBlock block={block} />;
    case 'barChart':
      return <BarChartBlock block={block} />;
    case 'donut':
      return <DonutBlock block={block} />;
    case 'rankBars':
      return <RankBarsBlock block={block} />;
    case 'scatter':
      return <ScatterBlock block={block} />;
    case 'table':
      return <TableBlock block={block} />;
    case 'callout':
      return <CalloutBlockAdapter block={block} />;
    case 'sectionHeading':
      return <SectionHeadingBlockAdapter block={block} />;
    case 'filter':
      return <FilterBlock block={block} onFilterChange={onFilterChange} disabled={!onFilterChange} />;
    default:
      return renderUnknownBlock(block);
  }
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

/**
 * Tailwind grid columns for a row of N KPI tiles (explicit classes so Tailwind keeps them).
 * Two columns on phones for every multi-tile row — one tile per row wasted most of a 375px
 * screen and pushed the rest of the answer below the fold. Desktop counts are unchanged.
 */
function kpiGridCols(count: number): string {
  if (count >= 4) return 'grid-cols-2 lg:grid-cols-4';
  if (count === 3) return 'grid-cols-2 sm:grid-cols-3';
  if (count === 2) return 'grid-cols-2';
  return 'grid-cols-1';
}

/**
 * Group consecutive `kpiStat` blocks so multiple KPI tiles share one responsive row;
 * every other block stays full-width. Works the same for the streaming render (the
 * grouping is recomputed as blocks arrive).
 */
function groupBlocks(blocks: HydratedBlock[]): { kpis?: HydratedBlock[]; block?: HydratedBlock }[] {
  const groups: { kpis?: HydratedBlock[]; block?: HydratedBlock }[] = [];
  for (const block of blocks) {
    const last = groups[groups.length - 1];
    if (block.type === 'kpiStat') {
      if (last?.kpis) last.kpis.push(block);
      else groups.push({ kpis: [block] });
    } else {
      groups.push({ block });
    }
  }
  return groups;
}

/**
 * The staggered list of hydrated blocks, reused for both the final spec and the
 * live (streaming) render where blocks arrive one at a time. Each row animates in
 * on mount; consecutive KPI tiles are laid out side by side.
 */
export const CloudAgentBlocks: React.FC<{
  blocks: HydratedBlock[];
  onFilterChange?: OnFilterChange;
}> = ({ blocks, onFilterChange }) => (
  <motion.div
    className="flex flex-col gap-4"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {groupBlocks(blocks).map((group, i) =>
      group.kpis ? (
        <motion.div
          key={i}
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          className={cn('grid gap-4', kpiGridCols(group.kpis.length))}
        >
          {group.kpis.map((block, j) => (
            <div key={j}>{renderBlock(block, onFilterChange)}</div>
          ))}
        </motion.div>
      ) : (
        <motion.div key={i} variants={itemVariants} initial="hidden" animate="visible">
          {WIDE_BLOCK_TYPES.has((group.block as HydratedBlock).type) ? (
            <BlockFrame>{renderBlock(group.block as HydratedBlock, onFilterChange)}</BlockFrame>
          ) : (
            renderBlock(group.block as HydratedBlock, onFilterChange)
          )}
        </motion.div>
      )
    )}
  </motion.div>
);

/**
 * Renders a hydrated Cloud Agent spec: the Answer hero card (title + summary + optional
 * keyPoints) followed by the blocks, each staggering
 * in via framer-motion. Blocks render from server-hydrated `data` only.
 *
 * Under the v2 depth ladder `title`/`summary` are optional — a `kpis`- or `single`-depth answer
 * carries its prose in the turn's own text instead — so the hero card is dropped entirely when
 * the spec has nothing to put in it.
 */
export const GenerativePage: React.FC<{
  spec: HydratedSpec;
  onFilterChange?: OnFilterChange;
}> = ({ spec, onFilterChange }) => {
  const { t } = useTranslation();
  const hasHeader = !!spec.title || !!spec.summary || (spec.keyPoints?.length ?? 0) > 0;
  return (
    <div className="flex flex-col gap-4">
      {/* Answer hero card (fixed presentation, not a block adapter). */}
      {hasHeader && (
        <div className="bg-background dark:bg-background-dark rounded-xl p-5">
          {spec.title && <h2 className={cn(primaryText, 'text-xl font-bold')}>{spec.title}</h2>}
          {spec.summary && <p className={cn(secondaryText, 'text-sm mt-2 leading-6')}>{spec.summary}</p>}
          {spec.keyPoints && spec.keyPoints.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-500 rounded p-4 mt-4">
              <div className={cn(secondaryText, 'text-[11px] uppercase tracking-wider mb-2 font-semibold')}>
                {t('cloudAgent.keyPoints')}
              </div>
              <ul className="list-disc list-inside space-y-1">
                {spec.keyPoints.map((point, i) => (
                  <li key={i} className={cn(primaryText, 'text-sm')}>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Generated blocks, staggered in. */}
      <CloudAgentBlocks blocks={spec.blocks} onFilterChange={onFilterChange} />
    </div>
  );
};

export default GenerativePage;
